<template>
  <v-card>
    <v-card-text>
      <v-form @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search">
        <v-row>
          <v-col>
            <v-text-field
              v-model="localProduct.name"
              :label="$tc('name')"
              :error-messages="nameErrors"/>
            <v-select
              v-model="localProduct.zone"
              :label="$tc('zone')"
              :items="zones"
              :error-messages="zoneErrors"/>
            <v-text-field
              v-model="localProduct.min_temp"
              :label="$tc('minimum_temperature')"
              type="number"
              step="0.1"
              :error-messages="minTempErrors">
              <template slot="append">
                &deg; {{ localProduct.temperature_scale }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.max_temp"
              :label="$tc('maximum_temperature')"
              type="number"
              step="0.1"
              :error-messages="maxTempErrors">
              <template slot="append">
                &deg; {{ localProduct.temperature_scale }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.start_temp"
              :label="$tc('default_starting_temperature')"
              type="number"
              step="0.1"
              :error-messages="startTempErrors">
              <template slot="append">
                &deg; {{ localProduct.temperature_scale }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.min_start_temp"
              :label="$tc('min_start_temp')"
              type="number"
              step="0.1"
              :error-messages="minStartTempErrors">
              <template slot="append">
                &deg; {{ localProduct.temperature_scale }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.max_start_temp"
              :label="$tc('max_start_temp')"
              type="number"
              step="0.1"
              :error-messages="maxStartTempErrors">
              <template slot="append">
                &deg; {{ localProduct.temperature_scale }}
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="user.can('view_rootentity')" class="root-only py-3">
          <v-col>
            <v-text-field
              v-model="localProduct.coefficient"
              :label="$tc('coefficient')"
              type="number"
              step="0.0001"
              :error-messages="coefficientErrors"/>
            <v-switch
              v-model="localProduct.add_by_default"
              :label="$tc('add_to_new_routes')"/>
            <v-text-field
              v-model="localProduct.product_detail.sku"
              :label="$tc('sku')"/>
            <v-text-field
              v-model="localProduct.product_detail.case_weight"
              :label="$tc('case_weight')"
              type="number"
              step="0.1">
              <template slot="append">
                {{ $t(localProduct.product_detail.weight_units) }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.product_detail.height"
              :label="$tc('height')"
              type="number"
              step="0.1">
              <template slot="append">
                {{ $t(localProduct.product_detail.dimension_units) }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.product_detail.width"
              :label="$tc('width')"
              type="number"
              step="0.1">
              <template slot="append">
                {{ $t(localProduct.product_detail.dimension_units) }}
              </template>
            </v-text-field>
            <v-text-field
              v-model="localProduct.product_detail.depth"
              :label="$tc('depth')"
              type="number"
              step="0.1">
              <template slot="append">
                {{ $t(localProduct.product_detail.dimension_units) }}
              </template>
            </v-text-field>
            <v-textarea
              v-model="localProduct.product_detail.product_configuration"
              :label="$tc('product_configuration')"
              outline/>
          </v-col>
        </v-row>

        <v-row class="mt-3">
          <v-col sm="6">
            <v-btn type="submit" color="primary" :loading="saving">
              <v-icon>mdi-save</v-icon>
              <span v-if="mode === 'create'"> {{ $t('create') }} </span>
              <span v-else> {{ $t('save_changes') }} </span>
            </v-btn>
            <v-btn v-if="mode === 'update' && user.can('view_rootentity')" color="danger" @click="onDelete" :loading="deleting">
              <v-icon>mdi-delete</v-icon>
              {{ $t('delete_product') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import { required } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'

export default {
  mixins: [
    CurrentUserMixin,
    ValidationMixin
  ],
  props: {
    product: {
      type: Object
    }
  },
  validations: {
    localProduct: {
      name: {
        required
      },
      zone: {
        required
      },
      min_temp: {
        required,
        lessThanMaxTemp: (value, vm) => {
          return value < vm.max_temp
        },
        lessThanStartingTemp: (value, vm) => {
          return value < vm.start_temp
        }
      },
      max_temp: {
        required,
        greaterThanStartingTemp: (value, vm) => {
          return value > vm.start_temp
        }
      },
      coefficient: {
        required,
        greaterThanZero: (value, vm) => {
          return value > 0
        }
      },
      start_temp: {
        required
      },
      min_start_temp: {
        required
      },
      max_start_temp: {
        required
      }
    }
  },
  computed: {
    mode () {
      return this.localProduct.id != null ? 'update' : 'create'
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.name,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    zoneErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.zone,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    minTempErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.min_temp,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localProduct.min_temp,
        rule: 'lessThanMaxTemp',
        message: this.$t('min_temp_less_than_max_temp')
      }, {
        prop: this.$v.localProduct.min_temp,
        rule: 'lessThanStartingTemp',
        message: this.$t('min_temp_less_than_start_temp')
      }])
    },
    maxTempErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.max_temp,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localProduct.max_temp,
        rule: 'greaterThanStartingTemp',
        message: this.$t('max_temp_greater_than_start_temp')
      }])
    },
    startTempErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.start_temp,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    minStartTempErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.min_start_temp,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    maxStartTempErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.max_start_temp,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    coefficientErrors () {
      return this.validationErrors([{
        prop: this.$v.localProduct.coefficient,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localProduct.coefficient,
        rule: 'greaterThanZero',
        message: this.$t('coefficient_greater_than_zero')
      }])
    }
  },
  data () {
    return {
      saving: false,
      deleting: false,
      zones: [
        'cooler',
        'freezer',
        'dry',
        'cab'
      ],
      localProduct: {}
    }
  },
  methods: {
    async onSubmit (e) {
      this.$v.$touch()
      if (this.$v.$error) {
        return false
      }
      this.saving = true
      try {
        if (this.localProduct.customer_id == null) {
          // Use the customer context that was selected at the time the form was submitted.
          this.localProduct.customer_id = this.customerContext.id
        }
        const product = await this.$orm('Product').createOrUpdate(this.localProduct)
        this.$emit('saved', product)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.saving = false
      }
    },
    async onDelete (e) {
      await this.$store.dispatch('globalMessagingModule/clearFlashBanners')
      this.deleting = true
      try {
        await this.$orm('Product').destroy(this.$route.params.id)
        this.$router.push('/products', () => {
          // Set flash after the router has completed so it doesn't get cleared in afterEach()
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('product')} ${this.$i18n.t('generic_delete_success')}`
          )
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.deleting = false
      }
    },
    customerContextChangeHandler (newCustomerContext, previousCustomerContext) {
      if (this.product.customer_id === undefined) {
        const originalScale = this.product.temperature_scale || previousCustomerContext.tempFormat()
        this.product.temperature_scale = newCustomerContext.tempFormat()
        this.localProduct.min_temp = TemperatureConverterUtil.to(this.localProduct.min_temp, originalScale, this.localProduct.temperature_scale)
        this.localProduct.max_temp = TemperatureConverterUtil.to(this.localProduct.max_temp, originalScale, this.localProduct.temperature_scale)
        this.localProduct.start_temp = TemperatureConverterUtil.to(this.localProduct.start_temp, originalScale, this.localProduct.temperature_scale)
      }
    }
  },
  watch: {
    product: {
      immediate: true,
      handler(newProduct) {
        if (newProduct && newProduct.id != null) {
          // For update: clone the product and ensure product_detail exists.
          const cloned = cloneDeep(newProduct)
          if (!cloned.product_detail) {
            cloned.product_detail = { product_configuration: '' }
          }
          if (!cloned.temperature_scale) {
            cloned.temperature_scale = this.customerContext.tempFormat()
          }
          this.localProduct = cloned
        } else {
          // For create: initialize with a default product record.
          this.localProduct = this.$orm('Product').createRecord({
            product_detail: { product_configuration: '' },
            temperature_scale: this.customerContext.tempFormat()
          })
        }
      }
    }
  }
}
</script>

<style scoped>
</style>
